// Ratings that nobody has given yet.
//
// `books.avg_rating` is `numeric(3,2)` with no default and no NOT NULL
// (live_schema_snapshot.sql:1794), so an unrated book carries null — and null
// coerced with `|| 0` becomes a 0.0 that renders as "nobody liked this" and
// sorts below every rated book. That is not what the data says. It says nothing
// at all.
//
// CLAUDE.md already states this rule for publication years: "treat date-less
// books as recent/unknown, not year-zero, so they do not sink in sort order."
// Same rule, different column.
//
// The display convention was already established across the codebase before
// this file existed — `useUserStats.jsx`, `StatsTab.jsx`, `ShareButton.jsx`,
// `SidebarWidgets.jsx` and `ProfileBlocks.jsx` all render an em dash. This
// centralises that spelling rather than inventing a new one.

export const RATING_UNKNOWN = '—';

// A neutral prior for ranking, so an unrated book sits mid-pack instead of
// last. Not invented here: `get_personalized_recommendations` has used 3.0 for
// exactly this since the schema snapshot —
// `COALESCE((reading_stats->'avg_rating')::TEXT, '3.0')`,
// live_schema_snapshot.sql:255.
export const NEUTRAL_RATING = 3.0;

/**
 * The rating as a number, or null when there isn't one.
 *
 * `treatZeroAsUnknown` is for `user_csv_stats.avg_rating`, which is
 * `numeric(3,2) DEFAULT 0` (live_schema_snapshot.sql:3014) and was written as a
 * literal 0 by CSVUploadModal whenever the import contained no rated books.
 * Those rows are already in the table. A real 0 is not otherwise reachable:
 * ratings are 1-5, and the only branch that produced 0 was the "rated nothing"
 * branch. Do NOT pass this flag for `books.avg_rating`, where 0 has never been
 * written and a future 0 would be a genuine value.
 */
export function knownRating(value, { treatZeroAsUnknown = false } = {}) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  if (treatZeroAsUnknown && n === 0) return null;
  return n;
}

/** For display. Returns the em dash when there is no rating to show. */
export function formatRating(value, { decimals = 1, treatZeroAsUnknown = false } = {}) {
  const n = knownRating(value, { treatZeroAsUnknown });
  return n === null ? RATING_UNKNOWN : n.toFixed(decimals);
}

/**
 * Mean of the ratings that exist, ignoring the ones that do not — rather than
 * counting a missing rating as 0 in both the total and the divisor, which drags
 * the average down by however many people have not rated anything.
 *
 * Returns null when nothing in the list has a rating, so the caller shows the
 * em dash instead of printing a 0.0 average of no data.
 */
export function averageRating(values, { treatZeroAsUnknown = false } = {}) {
  const known = (values || [])
    .map((v) => knownRating(v, { treatZeroAsUnknown }))
    .filter((n) => n !== null);
  if (!known.length) return null;
  return known.reduce((s, n) => s + n, 0) / known.length;
}
